/**
 * Druckbare Fahrzeug-QR-Karten (Jun-2026) — Carliner-Optik (106 × 56 mm) WIEDERHERGESTELLT
 * und um 3 QR-Codes erweitert (User-Wunsch).
 *
 * Es werden DREI Karten im bewährten Carliner-Design erzeugt:
 *   1) „FÜR SCHLÜSSEL"  — Schlüssel-Leitzahl klein & lesbar in der MITTE des QR
 *                         (für die Schlüssel-Inventur per Scanner), zusätzlich
 *                         groß links. QR kodiert den Schlüssel-Scan-Link.
 *   2) „VORNE LINKS"    — Fahrzeug-QR (alle Infos), Platzierung vorne links.
 *   3) „HINTEN RECHTS"  — Fahrzeug-QR (alle Infos), Platzierung hinten rechts.
 *
 * Layout je Karte (Querformat 106 × 56 mm), ALLES LINKSBÜNDIG:
 *   LINKS:  Positions-Label · Modell · Basisfarbe · Fg-Nr
 *           · großer „SCAN" + langer gefüllter Pfeil · CTA · große Nummer
 *   RECHTS: großer QR-Code (Schlüssel-QR mit Leitzahl-Overlay in der Mitte)
 *
 * Route: /listing/:id/qrcard
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import api from "../lib/api";
import { generateLeitzahl } from "../lib/keyLeitzahl";
import DispoLocationNote from "../components/listing/DispoLocationNote";

const CARD_W_MM = 106;
const CARD_H_MM = 56;

export default function ListingQrCardPage() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const autoPrint = searchParams.get("autoprint") === "1";
    const [listing, setListing] = useState(null);
    const [dispoLoc, setDispoLoc] = useState(null);
    const [standortOverride, setStandortOverride] = useState("");
    const [err, setErr] = useState("");
    const [genBusy, setGenBusy] = useState(false);

    useEffect(() => {
        api.get(`/listings/${id}`)
            .then((r) => { setListing(r.data); setDispoLoc(r.data?.dispo_location || null); })
            .catch((e) => setErr(e.response?.data?.detail || "Inserat nicht gefunden"));
    }, [id]);

    // Aktueller Standort aus der Disposition (User-Wunsch): wird als Standort auf die
    // QR-Karten übernommen — reiner Anzeige-/Druck-Override, OHNE Händler-Übergabe-Anfrage.
    useEffect(() => {
        setStandortOverride(searchParams.get("loc") || "");
    }, [searchParams]);

    // Auto-Druck (User-Wunsch): nach erfolgreichem Fahrzeug-Eingang werden die 3 QR-Karten
    // automatisch über den Browser-Druckdialog gedruckt. Wird auch im versteckten iframe genutzt.
    useEffect(() => {
        if (!autoPrint || !listing) return;
        const t = setTimeout(() => { try { window.print(); } catch { /* ignore */ } }, 800);
        return () => clearTimeout(t);
    }, [autoPrint, listing]);

    if (err) return <div className="p-12 text-rose-700" data-testid="qr-card-error">{err}</div>;
    if (!listing) return <div className="p-12">Lade…</div>;

    const PUBLIC_BASE = (process.env.REACT_APP_BACKEND_URL
        || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/+$/, "");
    const vehicleUrl = `${PUBLIC_BASE}/listing/${listing.id}`;
    const keyUrl = `${vehicleUrl}?inv=key`;
    const fzg = [listing.brand, listing.model].filter(Boolean).join(" ");
    const color = listing.color_base || listing.color || "";
    const leitzahl = (listing.key_depot || listing.leitzahl
        || generateLeitzahl({ vin: listing.vin, street: listing.street || listing.location })
        || "—").toString().slice(0, 12).toUpperCase();
    // Jun-2026 (User-Wunsch) — Auf den Karten erscheint die Fahrgestellnummer (FIN)
    // statt des Standorts. Der gewählte Standort wird weiterhin am Fahrzeug gespeichert
    // (Schlüsseleinwurf / Disposition), aber nicht mehr auf die QR-Karten gedruckt.
    const owner = listing.owner || {};
    const ownAddress = { street: owner.street, postal_code: owner.postal_code, location: owner.city };

    const TILES = [
        { key: "schluessel", label: "FÜR SCHLÜSSEL", url: keyUrl, center: leitzahl, cta: "Schlüssel per Scan inventarisieren", bigNr: leitzahl },
        { key: "vorne-links", label: "VORNE LINKS", url: vehicleUrl, center: null, cta: "Jetzt alle Infos zu diesem Fahrzeug auf Ihr Handy!", bigNr: leitzahl },
        { key: "hinten-rechts", label: "HINTEN RECHTS", url: vehicleUrl, center: null, cta: "Jetzt alle Infos zu diesem Fahrzeug auf Ihr Handy!", bigNr: leitzahl },
    ];

    /** Zeichnet EINE Carliner-Karte (106 × 56) an Offset (ox, oy) ins PDF. */
    const drawCard = (pdf, ox, oy, tile, qrDataUrl) => {
        const PAD = 4;
        const LEFT_W = 56;
        pdf.setDrawColor(210);
        pdf.setLineWidth(0.2);
        pdf.rect(ox, oy, CARD_W_MM, CARD_H_MM);
        pdf.setTextColor(0, 0, 0);

        let y = oy + PAD + 3.5;
        // Positions-Label (klein, fett, oben)
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.text(tile.label, ox + PAD, y);
        y += 4.5;

        // Titel
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        const titleLines = pdf.splitTextToSize(fzg || "—", LEFT_W - 2);
        pdf.text(titleLines, ox + PAD, y);
        y += titleLines.length * 4.4 + 1;

        if (color) {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(8);
            pdf.text("Farbe:", ox + PAD, y);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(9.5);
            pdf.text(String(color), ox + PAD + 11, y);
            y += 4.6;
        }
        if (listing.vin) {
            pdf.setFont("courier", "bold");
            pdf.setFontSize(7.5);
            pdf.text(`Fg-Nr: ${listing.vin}`, ox + PAD, y);
            y += 4.2;
        } else {
            pdf.setFont("courier", "bold");
            pdf.setFontSize(7.5);
            pdf.text("Fg-Nr: —", ox + PAD, y);
            y += 4.2;
        }

        // Jun-2026 (User-Wunsch) — Schlüsselnummer (Standort wurde durch die FIN ersetzt)
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.text("Schl.-Nr:", ox + PAD, y);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.text(String(leitzahl), ox + PAD + 13, y);
        y += 4.2;

        // SCAN + Pfeil
        const scanBaseY = oy + CARD_H_MM - 14;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(17);
        const scanW = pdf.getTextWidth("SCAN");
        pdf.setLineWidth(0.6);
        pdf.line(ox + PAD, scanBaseY - 6, ox + PAD + scanW, scanBaseY - 6);
        pdf.text("SCAN", ox + PAD, scanBaseY);
        pdf.line(ox + PAD, scanBaseY + 1.6, ox + PAD + scanW, scanBaseY + 1.6);
        const ay = scanBaseY - 2.6;
        const ax0 = ox + PAD + scanW + 3;
        const axTip = ox + PAD + LEFT_W - 0.5;
        const headLen = 7;
        pdf.setLineWidth(2);
        pdf.line(ax0, ay, axTip - headLen, ay);
        pdf.triangle(axTip - headLen, ay - 5, axTip - headLen, ay + 5, axTip, ay, "F");

        // CTA
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        const cta = pdf.splitTextToSize(tile.cta, LEFT_W);
        const ctaY = scanBaseY + 6;
        pdf.text(cta, ox + PAD, ctaY);

        // Große Nummer (Leitzahl) — nur Schlüssel-Karte
        if (tile.bigNr) {
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(20);
            const knY = Math.min(ctaY + cta.length * 3.2 + 8, oy + CARD_H_MM - PAD - 1);
            pdf.text(String(tile.bigNr), ox + PAD, knY);
        }

        // QR rechts
        const QR_SIZE = 40;
        const qrX = ox + CARD_W_MM - PAD - QR_SIZE;
        const qrY = oy + (CARD_H_MM - QR_SIZE) / 2;
        pdf.addImage(qrDataUrl, "PNG", qrX, qrY, QR_SIZE, QR_SIZE);

        // Leitzahl-Overlay in der Mitte des Schlüssel-QR
        if (tile.center) {
            const cx = qrX + QR_SIZE / 2;
            const cy = qrY + QR_SIZE / 2;
            const bw = QR_SIZE * 0.46;
            const bh = QR_SIZE * 0.22;
            pdf.setFillColor(255, 255, 255);
            pdf.rect(cx - bw / 2, cy - bh / 2, bw, bh, "F");
            pdf.setDrawColor(0);
            pdf.setLineWidth(0.3);
            pdf.rect(cx - bw / 2, cy - bh / 2, bw, bh, "S");
            pdf.setTextColor(0, 0, 0);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(tile.center.length > 4 ? 9 : 12);
            pdf.text(String(tile.center), cx, cy, { align: "center", baseline: "middle" });
        }
    };

    const downloadPdf = async () => {
        setGenBusy(true);
        try {
            const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
            const ox = (210 - CARD_W_MM) / 2;
            let oy = 16;
            for (const tile of TILES) {
                const qrDataUrl = await QRCode.toDataURL(tile.url, { errorCorrectionLevel: "H", margin: 0, width: 600 });
                drawCard(pdf, ox, oy, tile, qrDataUrl);
                oy += CARD_H_MM + 12;
            }
            const fname = `QR-Karten_${(fzg || "Fahrzeug").replace(/[^a-z0-9-_]+/gi, "_")}_${listing.id.slice(0, 8)}.pdf`;
            pdf.save(fname);
        } catch (e) {
            alert("PDF-Generierung fehlgeschlagen: " + (e.message || e));
        } finally {
            setGenBusy(false);
        }
    };

    // SCAN (groß) + langer Pfeil (Carliner-Optik, wiederhergestellt)
    const ScanCombo = () => (
        <div className="flex items-center gap-2 w-full" data-testid="qr-card-scan-combo">
            <div className="border-y-2 border-black leading-none py-0.5 shrink-0">
                <span className="text-[17pt] font-black tracking-tight text-black">SCAN</span>
            </div>
            <div className="flex-1 flex items-center min-w-0">
                <div className="flex-1 h-[2px] bg-black" />
                <svg width="16" height="22" viewBox="0 0 12 16" aria-hidden="true" className="-ml-px shrink-0">
                    <polygon points="0,0 0,16 12,8" fill="#000" />
                </svg>
            </div>
        </div>
    );

    const Card = ({ tile }) => (
        <div
            className="bg-white border border-gray-300 print:border print:border-gray-400 shadow-lg print:shadow-none mx-auto"
            style={{ width: `${CARD_W_MM}mm`, height: `${CARD_H_MM}mm`, padding: "4mm" }}
            data-testid={`qr-card-tile-${tile.key}`}
        >
            <div className="flex h-full gap-2 items-stretch">
                {/* LINKS */}
                <div className="flex flex-col items-start text-left" style={{ flex: "0 0 56mm" }}>
                    <span className="text-[8pt] font-black uppercase tracking-wider text-black" data-testid={`qr-card-pos-${tile.key}`}>{tile.label}</span>
                    <h1 className="text-[11pt] leading-tight font-black tracking-tight text-black mt-0.5">{fzg}</h1>
                    {color && <p className="text-[8pt] mt-0.5 text-black">Farbe: <span className="text-[9.5pt] font-black">{color}</span></p>}
                    <p className="text-[7.5pt] mt-0.5 text-black font-mono-num" data-testid={`qr-card-fin-${tile.key}`}>Fg-Nr: <strong>{listing.vin || "—"}</strong></p>
                    <p className="text-[7.5pt] mt-0.5 text-black" data-testid={`qr-card-schluesselnr-${tile.key}`}>Schl.-Nr: <strong>{leitzahl}</strong></p>
                    <div className="mt-auto w-full">
                        <ScanCombo />
                        <p className="text-[8pt] leading-tight text-black mt-1">{tile.cta}</p>
                        {tile.bigNr && (
                            <div className="text-[20pt] font-black leading-none tracking-tight text-black mt-1" data-testid={`qr-card-bignr-${tile.key}`}>{tile.bigNr}</div>
                        )}
                    </div>
                </div>
                {/* RECHTS — QR */}
                <div className="flex-1 flex items-center justify-center relative">
                    <div className="relative" style={{ width: "40mm", height: "40mm" }}>
                        <QRCodeSVG value={tile.url} size={220} level="H" includeMargin={false} style={{ width: "40mm", height: "40mm" }} data-testid={`qr-card-qr-${tile.key}`} />
                        {tile.center && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <span className="bg-white border border-black px-1.5 py-0.5 font-black text-black leading-none" style={{ fontSize: tile.center.length > 4 ? "9px" : "13px" }} data-testid="qr-card-key-leitzahl">{tile.center}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-200 print:bg-white" data-testid="qr-card-page">
            <div className="print:hidden bg-[#111827] text-white py-3 px-6 flex items-center justify-between gap-4 flex-wrap sticky top-0 z-10">
                <a href={`/listing/${listing.id}`} className="inline-flex items-center gap-1.5 text-sm hover:text-[#FFCC00]">
                    <ArrowLeft className="w-4 h-4" /> Zurück zum Inserat
                </a>
                <span className="text-xs opacity-80">QR-Karten · {CARD_W_MM} × {CARD_H_MM} mm · Schlüssel / Vorne Links / Hinten Rechts</span>
                <div className="flex items-center gap-2">
                    <Button onClick={downloadPdf} disabled={genBusy} className="bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-[#111827] rounded-sm" data-testid="qr-card-pdf-btn">
                        <Download className="w-4 h-4 mr-2" /> {genBusy ? "Lade…" : "Als PDF herunterladen"}
                    </Button>
                    <Button onClick={() => window.print()} variant="outline" className="rounded-sm border-white text-white hover:bg-white hover:text-[#111827]" data-testid="qr-card-print-btn">
                        <Printer className="w-4 h-4 mr-2" /> Drucken
                    </Button>
                </div>
            </div>

            <div className="print:hidden bg-white border-b border-gray-200 px-6 py-4" data-testid="qr-card-config-bar">
                <div className="max-w-md">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                        Standort vor dem Drucken festlegen
                    </div>
                    <DispoLocationNote
                        listingId={listing.id}
                        dispoLocation={dispoLoc}
                        ownAddress={ownAddress}
                        onSaved={(loc) => { setDispoLoc(loc); setStandortOverride(""); }}
                    />
                    <p className="mt-2 text-[11px] text-gray-500" data-testid="qr-card-standort-hint">
                        Schlüsselnummer: <strong className="text-[#111827]">{leitzahl}</strong> · Auf den Karten erscheint die <strong>Fahrgestellnummer</strong>. Der gewählte Standort wird am Fahrzeug gespeichert (Schlüsseleinwurf/Disposition).
                    </p>
                    {standortOverride && (
                        <p className="mt-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1 inline-flex items-center gap-1.5" data-testid="qr-card-current-standort">
                            <span className="font-bold">Aktueller Standort übernommen:</span> {standortOverride}
                        </p>
                    )}
                </div>
            </div>

            <div className="py-8 print:py-0 space-y-6 print:space-y-4" data-testid="qr-card-tiles">
                {TILES.map((tile) => <Card key={tile.key} tile={tile} />)}
            </div>

            <style>{`
                @media print {
                    @page { size: A4 portrait; margin: 10mm; }
                    body { background: white !important; }
                }
            `}</style>
        </div>
    );
}
